import { TourStepPlacement } from '../domain/tour-definition';

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface PlacementResult {
  x: number;
  y: number;
  resolved: TourStepPlacement;
}

const GAP = 16;
const MARGIN = 16;

export function rectFromDom(r: DOMRect): Rect {
  return {
    left: r.left,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  };
}

export function rectCenterX(r: Rect): number {
  return r.left + r.width / 2;
}

/**
 * Pure placement math — a direct port of Flutter's `computeTooltipPlacement`.
 * Tries [preferred], flips to the opposite side if it would clip past the
 * viewport, then clamps within bounds.
 */
export function computeTooltipPlacement(
  target: Rect,
  tooltipW: number,
  tooltipH: number,
  screenW: number,
  screenH: number,
  preferred: TourStepPlacement,
): PlacementResult {
  const minX = MARGIN;
  const maxXRaw = screenW - MARGIN - tooltipW;
  const maxX = maxXRaw < minX ? minX : maxXRaw;

  const minY = MARGIN;
  const maxYRaw = screenH - MARGIN - tooltipH;
  const maxY = maxYRaw < minY ? minY : maxYRaw;

  const spaceBelow = screenH - target.bottom;
  const spaceAbove = target.top;
  const spaceLeft = target.left;
  const spaceRight = screenW - target.right;

  let resolved = preferred;
  if (preferred === 'auto') {
    resolved = spaceBelow >= spaceAbove ? 'bottom' : 'top';
  } else if (preferred === 'bottom' && spaceBelow < tooltipH + GAP) {
    resolved = 'top';
  } else if (preferred === 'top' && spaceAbove < tooltipH + GAP) {
    resolved = 'bottom';
  } else if (preferred === 'left' && spaceLeft < tooltipW + GAP) {
    // No room to the left (target hugs the left edge, e.g. the left side-nav) —
    // flip to the right so the tooltip never covers the highlighted control.
    resolved = 'right';
  } else if (preferred === 'right' && spaceRight < tooltipW + GAP) {
    resolved = 'left';
  }

  let x: number;
  let y: number;
  const cx = rectCenterX(target);
  const cy = target.top + target.height / 2;
  switch (resolved) {
    case 'right':
      x = target.right + GAP;
      y = cy - tooltipH / 2;
      break;
    case 'left':
      x = target.left - GAP - tooltipW;
      y = cy - tooltipH / 2;
      break;
    case 'top':
      x = cx - tooltipW / 2;
      y = target.top - GAP - tooltipH;
      break;
    case 'bottom':
    case 'auto':
    default:
      x = cx - tooltipW / 2;
      y = target.bottom + GAP;
      break;
  }

  x = Math.min(Math.max(x, minX), maxX);
  y = Math.min(Math.max(y, minY), maxY);
  return { x, y, resolved };
}
